/**
 * Phase 18.16-B Batch 1 — Service pages admin sub-section.
 *
 * Single component, dropdown lets admin pick which service page to
 * edit (sea-freight / air-freight / shop-drop-ship / moving / business).
 * Per page: 3 sections (page_banner, intro, cta_band) + card manager.
 *
 * Backend: /api/admin/service-pages/{slug}/*
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, Edit3, Loader2, ChevronUp, ChevronDown, X,
  Ship, Plane, ShoppingBag, Truck, Briefcase,
} from "lucide-react";

const SLUGS = [
  { value: "sea-freight",    label: "Zeevracht",          icon: Ship },
  { value: "air-freight",    label: "Luchtvracht",        icon: Plane },
  { value: "shop-drop-ship", label: "Shop, Drop & Ship",  icon: ShoppingBag },
  { value: "moving",         label: "Verhuizing",         icon: Truck },
  { value: "business",       label: "Zakelijke zendingen",icon: Briefcase },
];

const ICON_OPTIONS = [
  "ship", "plane", "package", "truck", "car", "map", "briefcase", "user",
  "check", "clock", "shield", "banknote", "support", "info", "receipt", "zap",
];

const SECTIONS = [
  { key: "page_banner", label: "Page banner" },
  { key: "intro",       label: "Intro" },
  { key: "cta_band",    label: "CTA-band" },
];

export default function ServicePagesSubSection() {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const canEdit = hasPermission("website:edit") || hasPermission("admin:all");

  const [slug, setSlug] = useState(SLUGS[0].value);
  const [activeSection, setActiveSection] = useState("page_banner");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [savingCard, setSavingCard] = useState(false);

  const load = useCallback(async (newSlug) => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/service-pages/${newSlug}`);
      setData(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e, "Kon instellingen niet laden"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(slug); }, [load, slug]);

  // Section edit helpers
  const setField = (sec, k, v) => setData((d) => ({
    ...d,
    sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [k]: v } },
  }));
  const setListField = (sec, listKey, idx, k, v) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list[idx] = { ...(list[idx] || {}), [k]: v };
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });
  const addListItem = (sec, listKey, template) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list.push({ ...template });
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });
  const removeListItem = (sec, listKey, idx) => setData((d) => {
    const list = [...(d.sections[sec]?.[listKey] || [])];
    list.splice(idx, 1);
    return { ...d, sections: { ...d.sections, [sec]: { ...(d.sections[sec] || {}), [listKey]: list } } };
  });

  const saveSection = async (sec) => {
    if (!canEdit) return;
    setSavingSection(sec);
    try {
      const r = await adminApi.put(`/admin/service-pages/${slug}/${sec}`, {
        payload: data.sections[sec] || {},
      });
      if (r.data) setData(r.data);
      toast.success(`${labelOf(sec)} opgeslagen`);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingSection(null);
    }
  };

  // Cards
  const openNewCard = () => setEditCard({
    id: null, title: "", description: "", badge: "", icon: "package",
    link_url: "", sort_order: ((data?.cards?.length || 0) + 1) * 10, is_active: true,
  });
  const openEditCard = (c) => setEditCard({ ...c });

  const saveCard = async () => {
    if (!editCard || !editCard.title?.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    setSavingCard(true);
    try {
      const body = {
        title: editCard.title.trim(),
        description: editCard.description || "",
        badge: editCard.badge || "",
        icon: editCard.icon || "package",
        link_url: editCard.link_url || "",
        sort_order: Number(editCard.sort_order) || 100,
        is_active: !!editCard.is_active,
      };
      if (editCard.id) {
        const r = await adminApi.put(`/admin/service-pages/${slug}/cards/${editCard.id}`, body);
        setData((d) => ({
          ...d,
          cards: d.cards.map((c) => (c.id === editCard.id ? r.data.card : c)),
        }));
        toast.success("Card opgeslagen");
      } else {
        const r = await adminApi.post(`/admin/service-pages/${slug}/cards`, body);
        setData((d) => ({
          ...d,
          cards: [...d.cards, r.data.card].sort((a, b) => a.sort_order - b.sort_order),
        }));
        toast.success("Card aangemaakt");
      }
      setEditCard(null);
    } catch (e) {
      toast.error(formatApiError(e, "Opslaan mislukt"));
    } finally {
      setSavingCard(false);
    }
  };

  const toggleCard = async (card) => {
    try {
      const r = await adminApi.put(`/admin/service-pages/${slug}/cards/${card.id}`, {
        is_active: !card.is_active,
      });
      setData((d) => ({
        ...d,
        cards: d.cards.map((c) => (c.id === card.id ? r.data.card : c)),
      }));
      toast.success(card.is_active ? "Card gedeactiveerd" : "Card geactiveerd");
    } catch (e) {
      toast.error(formatApiError(e, "Wijzigen mislukt"));
    }
  };

  const moveCard = async (card, dir) => {
    const cards = data.cards || [];
    const idx = cards.findIndex((c) => c.id === card.id);
    const target = idx + dir;
    if (target < 0 || target >= cards.length) return;
    const newList = [...cards];
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    const payload = newList.map((c, i) => ({ id: c.id, sort_order: (i + 1) * 10 }));
    setData((d) => ({ ...d, cards: newList.map((c, i) => ({ ...c, sort_order: (i + 1) * 10 })) }));
    try {
      await adminApi.post(`/admin/service-pages/${slug}/cards/reorder`, { cards: payload });
    } catch (e) {
      toast.error(formatApiError(e, "Sortering mislukt"));
      load(slug);
    }
  };

  if (loading || !data) {
    return <div className="py-10 text-center text-gray-400 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Service-page instellingen laden...</div>;
  }

  const activeSec = data.sections[activeSection] || {};

  return (
    <div data-testid="service-pages-sub-section">
      {!canEdit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
          U heeft alleen leesrechten — wijzigen vereist Owner/Admin of Content Manager.
        </div>
      )}

      {/* Slug picker */}
      <div className="flex flex-wrap gap-2 mb-6">
        {SLUGS.map((s) => {
          const Icon = s.icon;
          const active = slug === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => { setSlug(s.value); setActiveSection("page_banner"); setEditCard(null); }}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
              data-testid={`service-page-slug-${s.value}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-slate-200 pb-3">
        {SECTIONS.map((sec) => (
          <button
            key={sec.key}
            type="button"
            onClick={() => setActiveSection(sec.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeSection === sec.key ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
            data-testid={`service-page-section-${sec.key}`}
          >
            {sec.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setActiveSection("__cards__")}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${activeSection === "__cards__" ? "bg-[#082c50] text-white" : "bg-slate-100 text-[#082c50] hover:bg-slate-200"}`}
          data-testid="service-page-section-cards"
        >
          Voordelen-cards ({data.cards?.length || 0})
        </button>
      </div>

      {activeSection !== "__cards__" && (
        <SectionForm
          sectionKey={activeSection}
          values={activeSec}
          slug={slug}
          canEdit={canEdit}
          saving={savingSection === activeSection}
          onSave={() => saveSection(activeSection)}
          onChange={(k, v) => setField(activeSection, k, v)}
          onListChange={(listKey, idx, k, v) => setListField(activeSection, listKey, idx, k, v)}
          onListAdd={(listKey, tpl) => addListItem(activeSection, listKey, tpl)}
          onListRemove={(listKey, idx) => removeListItem(activeSection, listKey, idx)}
        />
      )}

      {activeSection === "__cards__" && (
        <CardsManager
          slug={slug}
          cards={data.cards || []}
          canEdit={canEdit}
          canDelete={isAdminOrOwner()}
          onNew={openNewCard}
          onEdit={openEditCard}
          onToggle={toggleCard}
          onMove={moveCard}
          editCard={editCard}
          savingCard={savingCard}
          setEditCard={setEditCard}
          onSaveCard={saveCard}
        />
      )}
    </div>
  );
}

function labelOf(key) {
  return (SECTIONS.find((s) => s.key === key) || {}).label || key;
}

// ============================================================
// Section form — top-level component (no inline definitions, focus-safe)
// ============================================================
function SectionForm({
  sectionKey, values, slug, canEdit, saving, onSave,
  onChange, onListChange, onListAdd, onListRemove,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid={`service-page-editor-${slug}-${sectionKey}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-chivo font-bold text-lg text-[#082c50]">{labelOf(sectionKey)}</h3>
        <Button
          onClick={onSave}
          disabled={!canEdit || saving}
          className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white"
          data-testid={`service-page-save-${sectionKey}`}
        >
          {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</> : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
        </Button>
      </div>

      {sectionKey === "page_banner" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} testid={`${slug}-banner-eyebrow`} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Hoofdtitel" v={values.title} onChange={(v) => onChange("title", v)} testid={`${slug}-banner-title`} />
            <TextRow label="Highlight (gradient)" v={values.highlight} onChange={(v) => onChange("highlight", v)} testid={`${slug}-banner-highlight`} />
          </div>
          <AreaRow label="Subtitle" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} testid={`${slug}-banner-subtitle`} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} />
          </div>
          <ListEditor
            label="USP-badges (max 5)"
            items={values.badges || []}
            fields={[
              { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
              { key: "label", label: "Label", type: "text" },
            ]}
            onChange={(idx, f, v) => onListChange("badges", idx, f, v)}
            onAdd={() => onListAdd("badges", { icon: "shield", label: "" })}
            onRemove={(idx) => onListRemove("badges", idx)}
            canEdit={canEdit}
            max={5}
          />
        </>
      )}

      {sectionKey === "intro" && (
        <>
          <TextRow label="Eyebrow" v={values.eyebrow} onChange={(v) => onChange("eyebrow", v)} />
          <TextRow label="Sectietitel (H2)" v={values.title} onChange={(v) => onChange("title", v)} />
          <AreaRow label="Subtitle / uitleg" v={values.subtitle} onChange={(v) => onChange("subtitle", v)} />
        </>
      )}

      {sectionKey === "cta_band" && (
        <>
          <TextRow label="Titel" v={values.title} onChange={(v) => onChange("title", v)} />
          <AreaRow label="Beschrijving" v={values.description} onChange={(v) => onChange("description", v)} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextRow label="Primary CTA label" v={values.cta_primary_label} onChange={(v) => onChange("cta_primary_label", v)} />
            <TextRow label="Primary CTA URL" v={values.cta_primary_url} onChange={(v) => onChange("cta_primary_url", v)} />
            <TextRow label="Secondary CTA label" v={values.cta_secondary_label} onChange={(v) => onChange("cta_secondary_label", v)} />
            <TextRow label="Secondary CTA URL" v={values.cta_secondary_url} onChange={(v) => onChange("cta_secondary_url", v)} />
          </div>
        </>
      )}
    </div>
  );
}

function TextRow({ label, v, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid ? `${testid}-input` : undefined} />
    </div>
  );
}
function AreaRow({ label, v, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Textarea rows={3} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid ? `${testid}-input` : undefined} />
    </div>
  );
}

function ListEditor({ label, items, fields, onChange, onAdd, onRemove, canEdit, max }) {
  return (
    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-[#082c50]">{label}</span>
        {canEdit && items.length < (max || 12) && (
          <Button size="sm" variant="outline" onClick={onAdd} className="rounded-lg text-xs">
            <Plus className="w-3 h-3 mr-1" /> Toevoegen
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-slate-200">
            {fields.map((f) => (
              <div key={f.key} className="flex-1 min-w-[140px]">
                <Label className="text-[10px] uppercase tracking-wide">{f.label}</Label>
                {f.type === "select" ? (
                  <select
                    value={it[f.key] || ""}
                    onChange={(e) => onChange(idx, f.key, e.target.value)}
                    disabled={!canEdit}
                    className="w-full h-9 px-2 rounded-lg border border-slate-200 text-sm"
                  >
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input value={it[f.key] || ""} onChange={(e) => onChange(idx, f.key, e.target.value)} disabled={!canEdit} />
                )}
              </div>
            ))}
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => onRemove(idx)} className="rounded-lg text-red-600">
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-gray-500 italic py-2">Nog geen items.</div>}
      </div>
    </div>
  );
}

// ============================================================
// Cards manager
// ============================================================
function CardsManager({ slug, cards, canEdit, canDelete, onNew, onEdit, onToggle, onMove, editCard, savingCard, setEditCard, onSaveCard }) {
  return (
    <div className="space-y-4" data-testid={`service-page-cards-${slug}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-chivo font-bold text-lg text-[#082c50]">Voordelen-cards</h3>
          <p className="text-xs text-gray-600 mt-1">Beheer de cards in het "Voor wie / hoe werkt" blok. Sortering via pijltjes; uitschakelen via toggle.</p>
        </div>
        {canEdit && (
          <Button onClick={onNew} className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white">
            <Plus className="w-4 h-4 mr-1" /> Nieuwe card
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {cards.map((c, i) => (
          <div key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border ${c.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 opacity-70"}`} data-testid={`service-page-card-row-${c.id}`}>
            <div className="flex flex-col gap-1">
              <button disabled={!canEdit || i === 0} onClick={() => onMove(c, -1)} className="p-1 text-slate-500 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
              <button disabled={!canEdit || i === cards.length - 1} onClick={() => onMove(c, 1)} className="p-1 text-slate-500 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
            </div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#082c50] via-[#1a5193] to-[#194d8d] text-white flex items-center justify-center text-[10px] font-bold uppercase">{c.icon || "?"}</div>
            <div className="flex-1 min-w-0">
              <div className="font-chivo font-bold text-sm text-[#082c50] truncate">{c.title}</div>
              <div className="text-xs text-gray-600 truncate">{c.description}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{c.badge || "—"} · sort {c.sort_order}</div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={c.is_active} disabled={!canEdit} onCheckedChange={() => onToggle(c)} />
              <Button size="sm" variant="outline" disabled={!canEdit} onClick={() => onEdit(c)} className="rounded-xl">
                <Edit3 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {cards.length === 0 && (
          <div className="py-10 text-center text-gray-500 border border-dashed border-slate-300 rounded-xl">
            Nog geen cards. Klik <strong>Nieuwe card</strong> om te beginnen.
          </div>
        )}
      </div>

      {editCard && (
        <div className="mt-4 p-5 rounded-2xl border border-[#082c50]/30 bg-blue-50/50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-chivo font-bold text-[#082c50]">{editCard.id ? "Card bewerken" : "Nieuwe card"}</h4>
            <button onClick={() => setEditCard(null)} className="p-1 text-slate-500 hover:text-[#082c50]"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Titel</Label>
              <Input value={editCard.title || ""} onChange={(e) => setEditCard({ ...editCard, title: e.target.value })} data-testid={`service-card-title-input-${slug}`} />
            </div>
            <div className="col-span-2">
              <Label>Beschrijving</Label>
              <Textarea rows={2} value={editCard.description || ""} onChange={(e) => setEditCard({ ...editCard, description: e.target.value })} />
            </div>
            <div>
              <Label>Badge / korte label</Label>
              <Input value={editCard.badge || ""} onChange={(e) => setEditCard({ ...editCard, badge: e.target.value })} />
            </div>
            <div>
              <Label>Icon</Label>
              <select value={editCard.icon || "package"} onChange={(e) => setEditCard({ ...editCard, icon: e.target.value })} className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm">
                {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Link URL (optioneel)</Label>
              <Input value={editCard.link_url || ""} onChange={(e) => setEditCard({ ...editCard, link_url: e.target.value })} />
            </div>
            <div>
              <Label>Sortering</Label>
              <Input type="number" value={editCard.sort_order || 100} onChange={(e) => setEditCard({ ...editCard, sort_order: e.target.value })} />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={!!editCard.is_active} onCheckedChange={(v) => setEditCard({ ...editCard, is_active: v })} />
              <span className="text-sm text-[#082c50]">Actief op de pagina</span>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditCard(null)} className="rounded-xl">Annuleren</Button>
            <Button onClick={onSaveCard} disabled={!canEdit || savingCard} className="rounded-xl bg-[#082c50] hover:bg-[#0a335c] text-white" data-testid={`service-card-save-${slug}`}>
              {savingCard ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Opslaan...</> : <><Save className="w-4 h-4 mr-1" />Opslaan</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
